/**
 * Event renderer for CLI output
 * Renders parsed events with colors and formatting
 *
 * Run lifecycle is rendered via:
 * - renderRunStarted: Called immediately after run is created
 * - renderRunCompleted: Called when run completes successfully
 * - renderRunFailed: Called when run fails
 */

import chalk from "chalk";
import type { ParsedEvent } from "./claude-event-parser";
import type { RunResult } from "../api/core/types";
import {
  getFrameworkDisplayName,
  isSupportedFramework,
} from "@vm0/core/frameworks";
import { formatIsoTimestamp } from "../utils/time-format";
import {
  formatToolHeader,
  formatToolResult,
  type ToolUseData,
  type ToolResultData,
} from "./tool-formatters";

/**
 * Info about a started run
 */
interface RunStartedInfo {
  runId: string;
  sandboxId?: string;
}

/**
 * Options for creating an EventRenderer instance
 */
interface EventRendererOptions {
  /** Whether to show timestamp prefix (useful for historical log viewing) */
  showTimestamp?: boolean;
  /** Whether to show verbose output (full tool inputs/outputs) */
  verbose?: boolean;
  /** Whether to buffer tool_use events and wait for tool_result (default: true for streaming) */
  buffered?: boolean;
}

function recordData(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function displayString(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

function displayNonNegativeNumber(value: unknown): number {
  const number =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number(value)
        : undefined;
  return number !== undefined && Number.isFinite(number) && number >= 0
    ? number
    : 0;
}

/**
 * Stateful event renderer that buffers tool_use events
 * and displays them grouped with their tool_result
 */
export class EventRenderer {
  private pendingToolUse = new Map<
    string,
    { toolUse: ToolUseData; prefix: string; rendered: boolean }
  >();
  private ambiguousToolUseIds = new Set<string>();
  private ambiguousRenderedToolUses = new Map<string, ToolUseData[]>();
  private options: EventRendererOptions;
  private lastEventType: string | null = null;
  private frameworkDisplayName: string = "Agent";

  constructor(options?: EventRendererOptions) {
    this.options = options ?? {};
  }

  /**
   * Render run started info
   * Called immediately after run is created, before polling events
   */
  static renderRunStarted(info: RunStartedInfo): void {
    console.log(chalk.bold("▶ Run started"));
    console.log(`  Run ID:   ${chalk.dim(info.runId)}`);
    if (info.sandboxId) {
      console.log(`  Sandbox:  ${chalk.dim(info.sandboxId)}`);
    }
    console.log(chalk.dim(`  (use "zero logs ${info.runId}" to view logs)`));
    console.log();
  }

  /**
   * Format timestamp for display (without milliseconds, matching metrics format)
   */
  static formatTimestamp(timestamp: Date): string {
    return formatIsoTimestamp(timestamp);
  }

  /**
   * Render a parsed event to console
   */
  render(event: ParsedEvent): void {
    const timestampPrefix = this.options.showTimestamp
      ? `[${EventRenderer.formatTimestamp(event.timestamp)}] `
      : "";

    switch (event.type) {
      case "init":
        this.renderPendingToolUses();
        this.renderInit(event, timestampPrefix);
        break;
      case "text":
        this.renderPendingToolUses();
        this.renderText(event, timestampPrefix);
        break;
      case "tool_use":
        this.handleToolUse(event, timestampPrefix);
        break;
      case "tool_result":
        this.handleToolResult(event, timestampPrefix);
        break;
      case "result":
        this.renderPendingToolUses();
        this.renderResult(event, timestampPrefix);
        break;
    }
  }

  /**
   * Render any buffered display state that cannot wait for a future event.
   */
  flush(): void {
    this.renderPendingToolUses();
    this.pendingToolUse.clear();
    this.ambiguousToolUseIds.clear();
    this.ambiguousRenderedToolUses.clear();
  }

  private renderPendingToolUses(): void {
    for (const pending of this.pendingToolUse.values()) {
      if (pending.rendered) {
        continue;
      }
      const { toolUse, prefix } = pending;
      this.renderToolUseOnly(toolUse, prefix);
      pending.rendered = true;
    }
  }

  /**
   * Render run completed state
   * Note: This is run lifecycle status, not an event
   */
  static renderRunCompleted(result: RunResult | undefined): void {
    // Visual separator to distinguish from event stream
    console.log("");
    console.log(chalk.green("✓ Run completed successfully"));

    if (result) {
      console.log(`  Checkpoint:    ${chalk.dim(result.checkpointId)}`);
      console.log(`  Session:       ${chalk.dim(result.agentSessionId)}`);
      console.log(`  Conversation:  ${chalk.dim(result.conversationId)}`);

      // Render artifact and volumes
      if (result.artifact && Object.keys(result.artifact).length > 0) {
        console.log(`  Artifact:`);
        for (const [name, version] of Object.entries(result.artifact)) {
          console.log(
            `    ${name}: ${chalk.dim(EventRenderer.formatVersion(version))}`,
          );
        }
      }

      if (result.volumes && Object.keys(result.volumes).length > 0) {
        console.log(`  Volumes:`);
        for (const [name, version] of Object.entries(result.volumes)) {
          console.log(
            `    ${name}: ${chalk.dim(EventRenderer.formatVersion(version))}`,
          );
        }
      }
    }
  }

  /**
   * Render run failed state
   * Note: This is run lifecycle status, not an event
   */
  static renderRunFailed(error: string | undefined, runId: string): void {
    // Visual separator to distinguish from event stream
    console.error("");
    console.error(chalk.red("✗ Run failed"));

    // Parse markdown links from the error message and render them as
    // plain URLs that terminals can auto-detect as clickable.
    const errorText = error || "Unknown error";
    const linkMatch = errorText.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      const messageWithoutLink = errorText
        .replace(linkMatch[0], "")
        .replace(/\s+$/, "");
      console.error(`  Error: ${chalk.red(messageWithoutLink)}`);
      console.error(`  ${linkMatch[1]}: ${chalk.cyan(linkMatch[2])}`);
    } else {
      console.error(`  Error: ${chalk.red(errorText)}`);
    }

    console.error(chalk.dim(`  (use "zero logs ${runId}" to view agent logs)`));
  }

  /**
   * Handle tool_use event - buffer it for later grouping with result (when buffered)
   * or render immediately (when not buffered, e.g., historical log viewing)
   */
  private handleToolUse(event: ParsedEvent, prefix: string): void {
    const toolUseId = displayString(event.data.toolUseId);
    const tool = displayString(event.data.tool);
    const input = recordData(event.data.input);
    const toolUseData: ToolUseData = { tool, input };

    // When buffered (default), store for later grouping
    // When not buffered, render immediately
    if (this.options.buffered !== false && toolUseId.length > 0) {
      const existing = this.pendingToolUse.get(toolUseId);
      if (existing || this.ambiguousToolUseIds.has(toolUseId)) {
        if (existing) {
          if (!existing.rendered) {
            this.renderToolUseOnly(existing.toolUse, existing.prefix);
          }
          this.rememberAmbiguousRenderedToolUse(toolUseId, existing.toolUse);
        }
        this.pendingToolUse.delete(toolUseId);
        this.ambiguousToolUseIds.add(toolUseId);
        this.renderToolUseOnly(toolUseData, prefix);
        this.rememberAmbiguousRenderedToolUse(toolUseId, toolUseData);
        return;
      }
      this.pendingToolUse.set(toolUseId, {
        toolUse: toolUseData,
        prefix,
        rendered: false,
      });
    } else {
      // Non-buffered, or malformed tool_use without a stable id: render immediately.
      this.renderToolUseOnly(toolUseData, prefix);
    }
  }

  /**
   * Render a tool_use event without waiting for result (for historical log viewing)
   */
  private renderToolUseOnly(toolUse: ToolUseData, prefix: string): void {
    // Add spacing before tool if previous was text
    if (this.lastEventType === "text") {
      console.log();
    }

    const cont = this.getContinuationPrefix();
    const headerLines = formatToolHeader(toolUse);

    // First line gets the bullet, rest get simple indent
    for (let i = 0; i < headerLines.length; i++) {
      if (i === 0) {
        console.log(prefix + "● " + headerLines[i]);
      } else {
        console.log(cont + headerLines[i]);
      }
    }
    console.log(); // Empty line after each tool_use
    this.lastEventType = "tool";
  }

  /**
   * Handle tool_result event - lookup buffered tool_use and render grouped
   */
  private handleToolResult(event: ParsedEvent, prefix: string): void {
    const toolUseId = displayString(event.data.toolUseId);
    const result = displayString(event.data.result);
    const isError = Boolean(event.data.isError);

    if (toolUseId.length > 0 && this.ambiguousToolUseIds.has(toolUseId)) {
      const orphanToolUse = this.getToolUseFromResultEvent(event);
      const renderedToolUse = orphanToolUse
        ? this.getMatchingAmbiguousRenderedToolUse(toolUseId, orphanToolUse)
        : undefined;
      if (renderedToolUse) {
        this.renderToolResultOnly(renderedToolUse, { result, isError }, prefix);
        return;
      }
      if (orphanToolUse) {
        this.renderGroupedTool(orphanToolUse, { result, isError }, prefix);
      }
      return;
    }

    const pending = this.pendingToolUse.get(toolUseId);

    if (pending) {
      if (pending.rendered) {
        this.renderToolResultOnly(pending.toolUse, { result, isError }, prefix);
      } else {
        // Render grouped output
        this.renderGroupedTool(pending.toolUse, { result, isError }, prefix);
      }
      this.pendingToolUse.delete(toolUseId);
      return;
    }

    const orphanToolUse = this.getToolUseFromResultEvent(event);
    if (orphanToolUse) {
      this.renderGroupedTool(orphanToolUse, { result, isError }, prefix);
    }
    // Skip orphan tool_results without enough tool metadata to render.
  }

  private getToolUseFromResultEvent(event: ParsedEvent): ToolUseData | null {
    const tool = event.data.tool;
    if (typeof tool !== "string" || tool.length === 0) {
      return null;
    }

    return {
      tool,
      input: recordData(event.data.input),
    };
  }

  private rememberAmbiguousRenderedToolUse(
    toolUseId: string,
    toolUse: ToolUseData,
  ): void {
    const renderedToolUses = this.ambiguousRenderedToolUses.get(toolUseId);
    if (renderedToolUses) {
      renderedToolUses.push(toolUse);
      return;
    }
    this.ambiguousRenderedToolUses.set(toolUseId, [toolUse]);
  }

  private getMatchingAmbiguousRenderedToolUse(
    toolUseId: string,
    toolUse: ToolUseData,
  ): ToolUseData | undefined {
    const renderedToolUses = this.ambiguousRenderedToolUses.get(toolUseId);
    if (!renderedToolUses) {
      return undefined;
    }

    for (let index = renderedToolUses.length - 1; index >= 0; index -= 1) {
      const renderedToolUse = renderedToolUses[index];
      if (
        renderedToolUse !== undefined &&
        this.hasSameToolHeader(renderedToolUse, toolUse)
      ) {
        return renderedToolUse;
      }
    }
    return undefined;
  }

  private hasSameToolHeader(first: ToolUseData, second: ToolUseData): boolean {
    return (
      formatToolHeader(first).join("\n") === formatToolHeader(second).join("\n")
    );
  }

  /**
   * Get continuation prefix (simple indent, no timestamp alignment)
   */
  private getContinuationPrefix(): string {
    return "  ";
  }

  /**
   * Render grouped tool output (tool_use + tool_result together)
   */
  private renderGroupedTool(
    toolUse: ToolUseData,
    result: ToolResultData,
    prefix: string,
  ): void {
    // Add spacing before tool if previous was text
    if (this.lastEventType === "text") {
      console.log();
    }

    const verbose = this.options.verbose ?? false;
    const cont = this.getContinuationPrefix();

    const headerLines = formatToolHeader(toolUse);
    const resultLines = formatToolResult(toolUse, result, verbose);

    // First line gets timestamp + bullet, rest get simple indent
    for (let i = 0; i < headerLines.length; i++) {
      if (i === 0) {
        console.log(prefix + "● " + headerLines[i]);
      } else {
        console.log(cont + headerLines[i]);
      }
    }
    for (const line of resultLines) {
      console.log(cont + line);
    }
    console.log(); // Empty line after each group
    this.lastEventType = "tool";
  }

  private renderToolResultOnly(
    toolUse: ToolUseData,
    result: ToolResultData,
    prefix: string,
  ): void {
    if (this.lastEventType === "text") {
      console.log();
    }

    const verbose = this.options.verbose ?? false;
    const cont = this.getContinuationPrefix();
    const resultLines = formatToolResult(toolUse, result, verbose);

    for (let i = 0; i < resultLines.length; i++) {
      const line = resultLines[i];
      if (i === 0) {
        console.log(prefix + cont + line);
      } else {
        console.log(cont + line);
      }
    }
    console.log();
    this.lastEventType = "tool";
  }

  private renderInit(event: ParsedEvent, prefix: string): void {
    const frameworkStr = displayString(event.data.framework) || "claude-code";
    const displayName = isSupportedFramework(frameworkStr)
      ? getFrameworkDisplayName(frameworkStr)
      : frameworkStr;
    this.frameworkDisplayName = displayName;
    console.log(prefix + chalk.bold(`▷ ${displayName} Started`));
    console.log(`  Session: ${chalk.dim(displayString(event.data.sessionId))}`);
    if (event.data.model) {
      console.log(`  Model: ${chalk.dim(String(event.data.model))}`);
    }
    console.log(
      `  Tools: ${chalk.dim(
        Array.isArray(event.data.tools)
          ? event.data.tools.join(", ")
          : String(event.data.tools || ""),
      )}`,
    );
    console.log();
    this.lastEventType = "init";
  }

  private renderText(event: ParsedEvent, prefix: string): void {
    const text = displayString(event.data.text);
    // Text events get a bullet prefix
    console.log(prefix + "● " + text);
    this.lastEventType = "text";
  }

  private renderResult(event: ParsedEvent, prefix: string): void {
    console.log(); // Spacing before result
    const success = Boolean(event.data.success);
    const eventFramework = displayString(event.data.framework);
    const displayName =
      eventFramework && isSupportedFramework(eventFramework)
        ? getFrameworkDisplayName(eventFramework)
        : eventFramework || this.frameworkDisplayName;
    this.frameworkDisplayName = displayName;

    if (success) {
      console.log(prefix + chalk.bold(`◆ ${displayName} Completed`));
    } else {
      console.log(prefix + chalk.bold(`◆ ${displayName} Failed`));
      const result = displayString(event.data.result).trim();
      if (result.length > 0) {
        const [firstLine, ...restLines] = result.split("\n");
        console.log(`  Error: ${chalk.red(firstLine)}`);
        for (const line of restLines) {
          console.log(`         ${chalk.red(line)}`);
        }
      }
    }

    const durationMs = displayNonNegativeNumber(event.data.durationMs);
    const durationSec = (durationMs / 1000).toFixed(1);
    console.log(`  Duration: ${chalk.dim(durationSec + "s")}`);

    const numTurns = displayNonNegativeNumber(event.data.numTurns);
    console.log(`  Turns: ${chalk.dim(String(numTurns))}`);

    const usage = event.data.usage as Record<string, unknown>;
    if (usage && typeof usage === "object") {
      const inputTokens = displayNonNegativeNumber(usage.input_tokens);
      const outputTokens = displayNonNegativeNumber(usage.output_tokens);

      const formatTokens = (count: number): string => {
        if (count >= 1000) {
          return Math.floor(count / 1000) + "k";
        }
        return String(count);
      };

      console.log(
        `  Tokens: ${chalk.dim(
          `input=${formatTokens(inputTokens)} output=${formatTokens(outputTokens)}`,
        )}`,
      );
    }
    this.lastEventType = "result";
  }

  /**
   * Format version ID for display (show short 8-character prefix)
   */
  private static formatVersion(version: string): string {
    // SHA-256 hashes are 64 characters, show first 8
    if (version.length === 64 && /^[a-f0-9]+$/i.test(version)) {
      return version.slice(0, 8);
    }
    // For "latest" or other formats, show as-is
    return version;
  }
}
